import React, { Attributes, useEffect, useMemo } from 'react';
import { useQuery } from '@apollo/client/react';
import { findSectionModules } from '../../lib/findSectionModules';
import { throwQueryError } from '../../lib/apollo/throwQueryError';
import { useCustomPageVariables } from '../../hooks/useCustomPageVariables';
import { Loading } from '../../components/Loading';
import { LazyModuleSection } from '../../components/LazyModuleSection';
import { NotFoundPage } from '../NotFoundPage';
import { CUSTOM_PAGE_QUERY } from '../HomePage';
import { tc } from '../../lib/trackingContext';
import { EditorialPageMeta } from './EditorialPageMeta';

interface Props extends Attributes {
  slug?: string;
}

export const EditorialPage: React.FC<Props> = ({ slug }) => {
  const customPageVariables = useCustomPageVariables({ slug });

  const { data, loading, error } = useQuery(CUSTOM_PAGE_QUERY, {
    variables: customPageVariables
  });

  const topModules = useMemo(() => findSectionModules(data, 'top'), [data]);
  const headerModules = useMemo(() => findSectionModules(data, 'header'), [data]);
  const bottomModules = useMemo(() => findSectionModules(data, 'bottom'), [data]);

  const modules = useMemo(
    () => [
      ...(topModules?.modules || []),
      ...(headerModules?.modules || []),
      ...(bottomModules?.modules || [])
    ],
    [topModules, headerModules, bottomModules]
  );

  useEffect(() => {
    tc.setCurrent('display_page_name', data?.displayPage?.name);
    return () => {
      tc.setCurrent('display_page_name', undefined);
    };
  });

  if (loading || !data) return <Loading />;
  if (error) throwQueryError(error);

  if (!data.displayPage) return <NotFoundPage />;

  return (
    <>
      <EditorialPageMeta displayPage={data.displayPage} />
      <LazyModuleSection modules={modules} />
    </>
  );
};
